import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useRef } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import { PageHeader, Btn, Input } from "@/shared/components/UI";
import { toast } from "sonner";
import {
  FolderPlus, FolderOpen, ArrowLeft, Download, Search, Lock, X, Eye,
  Presentation, Music, Video, Image as ImageIcon, FileText, File as FileIcon, Plus, Trash2,
  Loader2, Check, Upload,
} from "lucide-react";

const KIND = {
  presentation: { icon: Presentation, cls: "text-orange-600 bg-orange-50" },
  audio: { icon: Music, cls: "text-violet-600 bg-violet-50" },
  video: { icon: Video, cls: "text-rose-600 bg-rose-50" },
  image: { icon: ImageIcon, cls: "text-sky-600 bg-sky-50" },
  document: { icon: FileText, cls: "text-emerald-600 bg-emerald-50" },
  file: { icon: FileIcon, cls: "text-zinc-500 bg-zinc-100" },
};

const POCKET_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.m4a,.aac,.ogg,.flac,.mp4,.mov,.avi,.mkv,.webm,.txt,.csv,.zip,.ai,.psd,.eps,.svg";
const CHUNK_SIZE = 8 * 1024 * 1024;

async function uploadOne(file, code, onPct) {
  if (file.size > 6 * 1024 * 1024) {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    let last = null;
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(total));
      fd.append("filename", file.name);
      fd.append("project_code", code);
      fd.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), file.name);
      const { data } = await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      last = data;
      onPct(Math.round(((i + 1) / total) * 100));
    }
    return last;
  }
  const fd = new FormData();
  fd.append("file", file);
  fd.append("project_code", code);
  const { data } = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (e.total) onPct(Math.round((e.loaded / e.total) * 100)); },
  });
  return data;
}

function ensureExt(name, original) {
  const ext = original.includes(".") ? original.split(".").pop() : "";
  if (!ext) return name;
  return name.toLowerCase().endsWith(`.${ext.toLowerCase()}`) ? name : `${name}.${ext}`;
}

function fmtBytes(b) {
  if (!b) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

const extOf = (f) => ((f.name || f.url || "").split(".").pop() || "").toLowerCase();
const SHEET_EXTS = new Set(["xlsx", "xls", "csv", "ods"]);
const TEXT_EXTS = new Set(["txt", "md", "log", "json"]);
const VECTOR_EXTS = new Set(["ai", "eps", "cdr"]);
const canPreview = (f) =>
  ["image", "video", "audio"].includes(f.kind) || extOf(f) === "pdf" ||
  SHEET_EXTS.has(extOf(f)) || TEXT_EXTS.has(extOf(f)) || VECTOR_EXTS.has(extOf(f));

// Containers browsers can never decode natively — don't even try the player.
const NON_WEB_FORMATS = new Set(["avi", "mkv", "wma", "flv", "wmv"]);

function MediaFallback({ file, kind }) {
  return (
    <div className="w-full p-10 bg-white text-center" data-testid="pocket-preview-unplayable">
      <div className="text-sm font-semibold text-zinc-800 mb-1.5">
        This {kind} can't be opened in the browser
      </div>
      <p className="text-xs text-zinc-500 max-w-md mx-auto leading-relaxed">
        {kind === "video"
          ? <>The <b>.{extOf(file)}</b> format (or its codec) isn't supported by browser players. For in-portal playback, save/export the video as MP4 (H.264) before uploading — iPhone videos may need “Most Compatible” camera format.</>
          : kind === "audio"
          ? <>The <b>.{extOf(file)}</b> format isn't supported by browser players. For in-portal playback, save the audio as MP3, M4A or WAV before uploading.</>
          : kind === "design file"
          ? <>The <b>.{extOf(file)}</b> format can't be shown in a browser. Save/export it as <b>PDF</b> (or PDF-compatible .ai) for in-portal preview — or download it to open in CorelDRAW/Illustrator.</>
          : <>The <b>.{extOf(file)}</b> file couldn't be opened here — download it to view on your device.</>}
      </p>
      <a href={file.url} download={file.name} target="_blank" rel="noreferrer"
         data-testid="pocket-unplayable-download"
         className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700">
        <Download className="w-4 h-4" /> Download & open on your device
      </a>
    </div>
  );
}

function TextPreview({ file }) {
  const [state, setState] = useState({ loading: true, text: "", error: false });
  useEffect(() => {
    let dead = false;
    fetch(file.url).then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((t) => !dead && setState({ loading: false, text: t.slice(0, 200000), error: false }))
      .catch(() => !dead && setState({ loading: false, text: "", error: true }));
    return () => { dead = true; };
  }, [file.url]);
  if (state.loading) return <div className="w-full p-12 bg-white text-center text-sm text-zinc-400">Opening file…</div>;
  if (state.error) return <MediaFallback file={file} kind="file" />;
  return (
    <pre className="w-full bg-white text-xs text-zinc-800 whitespace-pre-wrap p-6 max-h-[72vh] overflow-auto font-mono leading-relaxed"
         data-testid="pocket-preview-text">{state.text || "(empty file)"}</pre>
  );
}

function VectorPreview({ file }) {
  const token = (file.url || "").split("/").pop();
  const [state, setState] = useState({ loading: true, src: "", error: false });
  useEffect(() => {
    let dead = false;
    fetch(`/api/files/${token}/preview`)
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => !dead && setState({ loading: false, src: URL.createObjectURL(b), error: false }))
      .catch(() => !dead && setState({ loading: false, src: "", error: true }));
    return () => { dead = true; };
  }, [token]);
  if (state.loading) return <div className="w-full p-12 bg-white text-center text-sm text-zinc-400">Rendering design file…</div>;
  if (state.error) return <MediaFallback file={file} kind="design file" />;
  return (
    <div className="w-full bg-zinc-50 flex items-center justify-center p-4">
      <img src={state.src} alt={file.name} className="max-h-[72vh] max-w-full shadow border border-zinc-200 bg-white"
           data-testid="pocket-preview-vector" />
    </div>
  );
}

function ExcelPreview({ file }) {
  const [state, setState] = useState({ loading: true, error: false, sheets: [], active: 0 });
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const XLSX = await import("xlsx");
        const res = await fetch(file.url);
        if (!res.ok) throw new Error("fetch failed");
        const wb = XLSX.read(await res.arrayBuffer(), { type: "array" });
        const sheets = wb.SheetNames.map((name) => ({
          name,
          rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: "" }).slice(0, 500),
        }));
        if (!dead) setState({ loading: false, error: false, sheets, active: 0 });
      } catch {
        if (!dead) setState((s) => ({ ...s, loading: false, error: true }));
      }
    })();
    return () => { dead = true; };
  }, [file.url]);

  if (state.loading) return <div className="w-full p-12 bg-white text-center text-sm text-zinc-400">Opening spreadsheet…</div>;
  if (state.error || !state.sheets.length) return <MediaFallback file={file} kind="spreadsheet" />;
  const sheet = state.sheets[state.active];
  const [head, ...body] = sheet.rows.length ? sheet.rows : [[]];
  return (
    <div className="w-full bg-white" data-testid="pocket-preview-excel">
      {state.sheets.length > 1 && (
        <div className="flex gap-1 px-3 pt-2 border-b border-zinc-100 overflow-x-auto">
          {state.sheets.map((s, i) => (
            <button key={s.name} onClick={() => setState((st) => ({ ...st, active: i }))}
                    className={`px-3 py-1.5 text-xs font-medium rounded-t-lg whitespace-nowrap ${i === state.active ? "bg-emerald-50 text-emerald-700 border border-b-0 border-emerald-200" : "text-zinc-500 hover:text-zinc-800"}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="overflow-auto max-h-[70vh]">
        <table className="text-xs border-collapse min-w-full">
          <thead>
            <tr>{(head || []).map((c, i) => (
              <th key={i} className="sticky top-0 bg-zinc-50 border border-zinc-200 px-2.5 py-1.5 text-left font-semibold text-zinc-700 whitespace-nowrap">{String(c)}</th>
            ))}</tr>
          </thead>
          <tbody>
            {body.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-zinc-50/50" : ""}>
                {(head || []).map((_, j) => (
                  <td key={j} className="border border-zinc-100 px-2.5 py-1 text-zinc-700 whitespace-nowrap">{String(r[j] ?? "")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {sheet.rows.length >= 500 && (
          <div className="text-center text-[11px] text-zinc-400 py-2">Showing the first 500 rows — download for the full file.</div>
        )}
      </div>
    </div>
  );
}

function PreviewModal({ file, onClose }) {
  const ext = extOf(file);
  const isPdf = ext === "pdf";
  const isSheet = SHEET_EXTS.has(ext);
  const isText = TEXT_EXTS.has(ext);
  const isVector = VECTOR_EXTS.has(ext);
  const [mediaError, setMediaError] = useState(false);
  const unplayable = mediaError || NON_WEB_FORMATS.has(ext);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}
         data-testid="pocket-preview-modal">
      <div className={`bg-white rounded-2xl shadow-xl overflow-hidden w-full ${isPdf || isSheet || file.kind === "video" ? "max-w-5xl" : file.kind === "audio" ? "max-w-lg" : "max-w-4xl"}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-900 truncate">{file.name}</div>
            <div className="text-[11px] text-zinc-400">{fmtBytes(file.size)}{file.size ? " · " : ""}{file.uploaded_by} · {(file.uploaded_at || "").slice(0, 10)}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a href={file.url} download={file.name} target="_blank" rel="noreferrer"
               data-testid="pocket-preview-download"
               className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50">
              <Download className="w-4 h-4" /> Download
            </a>
            <button onClick={onClose} data-testid="pocket-preview-close"
                    className="p-1.5 rounded-lg hover:bg-zinc-100"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="bg-zinc-950/95 flex items-center justify-center">
          {file.kind === "image" ? (
            <img src={file.url} alt={file.name} className="max-h-[75vh] max-w-full object-contain" data-testid="pocket-preview-image" />
          ) : file.kind === "video" ? (
            unplayable ? <MediaFallback file={file} kind="video" /> : (
              <video src={file.url} controls autoPlay className="max-h-[75vh] w-full bg-black"
                     onError={() => setMediaError(true)} data-testid="pocket-preview-video" />
            )
          ) : file.kind === "audio" ? (
            unplayable ? <MediaFallback file={file} kind="audio" /> : (
              <div className="w-full p-8 bg-white flex flex-col items-center gap-4">
                <span className="w-14 h-14 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center">
                  <Music className="w-7 h-7" />
                </span>
                <audio src={file.url} controls autoPlay className="w-full"
                       onError={() => setMediaError(true)} data-testid="pocket-preview-audio" />
              </div>
            )
          ) : isSheet ? (
            <ExcelPreview file={file} />
          ) : isText ? (
            <TextPreview file={file} />
          ) : isVector ? (
            <VectorPreview file={file} />
          ) : isPdf ? (
            <iframe src={file.url} title={file.name} className="w-full h-[75vh] bg-white" data-testid="pocket-preview-pdf" />
          ) : (
            <div className="w-full p-10 bg-white text-center text-sm text-zinc-500">
              Preview isn't available for this file type — use Download to view it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PocketFiles() {
  const [projects, setProjects] = useState(null);
  const [active, setActive] = useState(null);   // {project_code, name}
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/pocket/projects")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);

  if (active) return <ProjectPocket project={active} onBack={() => setActive(null)} />;

  const list = (projects || []).filter((p) =>
    !q || `${p.project_code} ${p.name} ${p.location}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="pocket-files-page">
      <PageHeader kicker="ONE PLACE PER PROJECT" title="Pocket Files" />
      <p className="text-sm text-zinc-500 -mt-3 mb-6 max-w-2xl">
        Keep every PPT, voice note, video, and reference file discussed for a project in one shared place.
        Everyone on the team can <b>add, view and download</b> — audio, video, images and PDFs play right here.
        Only an admin can delete files, so the trail stays protected.
      </p>
      <div className="relative max-w-md mb-5">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project code, name, location…"
               className="pl-10" data-testid="pocket-search" />
      </div>
      {projects === null ? (
        <div className="text-zinc-400 text-sm py-10">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-zinc-400 text-sm py-10 text-center border border-dashed border-zinc-200 rounded-xl">
          No projects available.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <button key={p.project_code} onClick={() => setActive(p)}
                    data-testid={`pocket-project-${p.project_code}`}
                    className="text-left card-flat p-5 hover:border-zinc-400 transition-colors">
              <div className="font-mono text-xs text-zinc-500 tracking-wider mb-1"><ProjRef code={p.project_code} /></div>
              <div className="font-display text-lg font-bold tracking-tight">{p.name}</div>
              <div className="text-sm text-zinc-500 mb-3">{p.location || "—"}</div>
              <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                <FolderOpen className="w-4 h-4 text-amber-500" />
                {p.file_count} file{p.file_count !== 1 ? "s" : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectPocket({ project, onBack }) {
  const code = project.project_code;
  const [folders, setFolders] = useState(null);
  const [open, setOpen] = useState({});
  const [newFolder, setNewFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");

  const sq = search.trim().toLowerCase();
  const visible = !sq ? folders : (folders || []).map((f) => {
    if ((f.name || "").toLowerCase().includes(sq)) return f;
    const files = (f.files || []).filter((x) => (x.name || "").toLowerCase().includes(sq));
    return files.length ? { ...f, files } : null;
  }).filter(Boolean);

  function load() {
    api.get(`/projects/${code}/pocket`)
      .then((r) => setFolders(r.data.folders || []))
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"));
  }
  useEffect(load, [code]);

  async function createFolder() {
    const name = newFolder.trim();
    if (!name) { toast.error("Enter a folder name"); return; }
    setCreating(true);
    try {
      await api.post(`/projects/${code}/pocket/folders`, { name });
      toast.success(`Folder "${name}" created`);
      setNewFolder("");
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setCreating(false); }
  }

  return (
    <div data-testid={`pocket-project-view-${code}`}>
      <button onClick={onBack} data-testid="pocket-back"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-orange-600 mb-4">
        <ArrowLeft className="w-4 h-4" /> All projects
      </button>
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold">{code}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.name}</h1>
        <p className="text-zinc-500 mt-1 text-sm flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5" /> Add &amp; download — only an admin can delete files.
        </p>
      </div>

      <div className="card-flat p-4 mb-6 flex flex-wrap items-center gap-2">
        <FolderPlus className="w-4 h-4 text-amber-500" />
        <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") createFolder(); }}
               placeholder="New folder name (e.g. Client meeting 12 Jun)"
               className="h-10 max-w-sm" data-testid="pocket-new-folder-input" />
        <Btn onClick={createFolder} disabled={creating} data-testid="pocket-new-folder-btn">
          <Plus className="w-4 h-4" /> {creating ? "Creating…" : "Create folder"}
        </Btn>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search folders & files…"
                 className="h-10 pl-9 w-56 sm:w-64" data-testid="pocket-search-input" />
          {search && (
            <button onClick={() => setSearch("")} data-testid="pocket-search-clear"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {folders === null ? (
        <div className="text-zinc-400 text-sm py-10">Loading…</div>
      ) : folders.length === 0 ? (
        <div className="text-zinc-400 text-sm py-10 text-center border border-dashed border-zinc-200 rounded-xl">
          No folders yet. Create one above to start adding files.
        </div>
      ) : visible.length === 0 ? (
        <div className="text-zinc-400 text-sm py-10 text-center border border-dashed border-zinc-200 rounded-xl"
             data-testid="pocket-search-empty">
          Nothing matches “{search.trim()}” in this project's pocket.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((f) => (
            <Folder key={f.id} code={code} folder={f}
                    open={sq ? true : !!open[f.id]} onToggle={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}
                    onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function Folder({ code, folder, open, onToggle, onChanged }) {
  const { user } = useAuth();
  const isAdmin = (user?.role || "") === "admin";
  const [adding, setAdding] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(null);

  async function deleteFile(file) {
    if (!window.confirm(`Delete “${file.name}” from this folder? This cannot be undone.`)) return;
    setDeleting(file.id);
    try {
      await api.delete(`/projects/${code}/pocket/files/${file.id}`);
      toast.success("File deleted");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setDeleting(null); }
  }

  return (
    <div className="card-flat overflow-hidden" data-testid={`pocket-folder-${folder.id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors">
        <span className="flex items-center gap-2.5 min-w-0">
          <FolderOpen className="w-5 h-5 text-amber-500 shrink-0" />
          <span className="font-medium text-zinc-900 truncate">{folder.name}</span>
          <span className="text-xs text-zinc-400">· {folder.file_count} file{folder.file_count !== 1 ? "s" : ""}</span>
        </span>
        <span className="text-[11px] text-zinc-400 shrink-0">by {folder.created_by}</span>
      </button>
      {open && (
        <div className="border-t border-zinc-100 p-4 space-y-2">
          {(folder.files || []).length === 0 && !adding && (
            <div className="text-sm text-zinc-400 py-2">No files yet in this folder.</div>
          )}
          {(folder.files || []).map((file) => {
            const meta = KIND[file.kind] || KIND.file;
            const Icon = meta.icon;
            const viewable = canPreview(file);
            return (
              <div key={file.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-zinc-100 hover:bg-zinc-50"
                   data-testid={`pocket-file-${file.id}`}>
                <button type="button" onClick={() => viewable && setPreview(file)}
                        className={`flex items-center gap-3 min-w-0 text-left ${viewable ? "cursor-pointer" : "cursor-default"}`}
                        data-testid={`pocket-file-open-${file.id}`}>
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.cls}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-sm truncate ${viewable ? "text-zinc-800 hover:text-blue-700" : "text-zinc-800"}`}>{file.name}</span>
                    <span className="block text-[11px] text-zinc-400">
                      {fmtBytes(file.size)}{file.size ? " · " : ""}{file.uploaded_by} · {(file.uploaded_at || "").slice(0, 10)}
                    </span>
                  </span>
                </button>
                <span className="flex items-center gap-3 shrink-0">
                  {viewable && (
                    <button type="button" onClick={() => setPreview(file)}
                            data-testid={`pocket-view-${file.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-800">
                      <Eye className="w-4 h-4" /> View
                    </button>
                  )}
                  <a href={file.url} download={file.name} target="_blank" rel="noreferrer"
                     data-testid={`pocket-download-${file.id}`}
                     className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800">
                    <Download className="w-4 h-4" /> Download
                  </a>
                  {isAdmin && (
                    <button type="button" onClick={() => deleteFile(file)} disabled={deleting === file.id}
                            data-testid={`pocket-delete-${file.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-700 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" /> {deleting === file.id ? "Deleting…" : "Delete"}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
          {preview && <PreviewModal file={preview} onClose={() => setPreview(null)} />}

          {adding ? (
            <MultiFileAdd code={code} folderId={folder.id}
                          onDone={() => { setAdding(false); onChanged(); }}
                          onCancel={() => setAdding(false)} />
          ) : (
            <button onClick={() => setAdding(true)} data-testid={`pocket-add-file-${folder.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 mt-1">
              <Plus className="w-4 h-4" /> Add files (select multiple at once — PPT, audio, video, reference…)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MultiFileAdd({ code, folderId, onDone, onCancel }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const upd = (id, patch) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  async function pick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fresh = files.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f, name: f.name, size: f.size, pct: 0, status: "uploading", url: "",
    }));
    setItems((xs) => [...xs, ...fresh]);
    for (const it of fresh) {
      try {
        const data = await uploadOne(it.file, code, (p) => upd(it.id, { pct: p }));
        upd(it.id, { status: "done", url: data.url, size: data.size });
      } catch {
        upd(it.id, { status: "error" });
        toast.error(`${it.file.name}: upload failed`);
      }
    }
  }

  const ready = items.filter((x) => x.status === "done");
  const uploading = items.some((x) => x.status === "uploading");

  async function saveAll() {
    if (!ready.length) { toast.error("Upload at least one file first"); return; }
    setSaving(true);
    try {
      for (const it of ready) {
        await api.post(`/projects/${code}/pocket/files`, {
          folder_id: folderId, name: ensureExt((it.name || "File").trim(), it.file.name),
          url: it.url, size: it.size,
        });
      }
      toast.success(`${ready.length} file${ready.length !== 1 ? "s" : ""} added to the folder`);
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="rounded-lg border border-dashed border-zinc-300 p-3 space-y-2" data-testid="pocket-multi-add">
      <button type="button" onClick={() => inputRef.current?.click()}
              data-testid="pocket-multi-pick"
              className="w-full h-11 px-3 border border-dashed border-zinc-400 hover:border-blue-600 hover:bg-blue-50 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
        <Upload className="w-4 h-4" /> {items.length ? "Add more files…" : "Choose files (you can select many at once)"}
      </button>
      <input ref={inputRef} type="file" multiple accept={POCKET_ACCEPT} onChange={pick} className="hidden" />

      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-2" data-testid={`pocket-multi-row-${it.id}`}>
          <span className="w-7 h-7 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
            {it.status === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
              : it.status === "done" ? <Check className="w-3.5 h-3.5 text-emerald-600" />
              : <X className="w-3.5 h-3.5 text-rose-500" />}
          </span>
          <Input value={it.name} onChange={(e) => upd(it.id, { name: e.target.value })}
                 disabled={it.status === "error"} placeholder="File name"
                 className="h-9 flex-1" data-testid={`pocket-multi-name-${it.id}`} />
          <span className="text-[11px] text-zinc-400 font-mono w-20 text-right shrink-0">
            {it.status === "uploading" ? `${it.pct}%` : it.status === "error" ? "failed" : fmtBytes(it.size)}
          </span>
          <button type="button" onClick={() => setItems((xs) => xs.filter((x) => x.id !== it.id))}
                  className="text-zinc-400 hover:text-rose-600 shrink-0" data-testid={`pocket-multi-remove-${it.id}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      <div className="flex gap-2 pt-1">
        <Btn onClick={saveAll} disabled={saving || uploading || ready.length === 0} data-testid="pocket-multi-save">
          {saving ? "Adding…" : uploading ? "Uploading…"
            : `Add ${ready.length || ""} file${ready.length !== 1 ? "s" : ""} to folder`}
        </Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Btn>
      </div>
      <p className="text-[11px] text-zinc-400">Rename any file before adding — the original file type is kept automatically.</p>
    </div>
  );
}
